import { ref, onMounted, watch, computed } from '@vue/composition-api'
import store from '@/store'
import i18n from '@/libs/i18n'
import useCommon from "@/views/organization/useCommon"
import useNotifications from '@/composables/useNotifications'
import { useRouter } from '@core/utils/utils'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date as mongoDate } from '@/helpers/mongoHelpers'

export default function useCustomersOpinions() {
    const { getLocationsForDropDown, locations, getPhysicalLocationsForDropDown, physical_locations, getFeedbackPointsForDropDown, feedbackPoints, formatIsoDate } = useCommon()
    const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { router } = useRouter()
    const { getItemsAggregate, updateItem } = useLambda()
    const isOnline = computed(() => store.state.app.isOnline)
    const isLoading = ref(true)
    const opinionId = ref(null)
    const searchQuery = ref('')
    const totalOpinions = ref(0)
    const selectedOpinion = ref('')
    const customersOpinions = ref([])
    const selectedLocations = ref('')
    const selectedPhysicalLocations = ref('')
    const selectedFeedbackPoints = ref('')
    const stateOpinionOptions = ref(['positive', 'neutral', 'negative'])
    const dataExcel = ref([])
    const userData = store.state?.userStore?.userData;
    const { role } = userData;
    const clientId = userData.role !== "admin" ? userData.client.$oid : null
    const collection = 'customer_opinion'

    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    const lastDayOfMonthDate = new Date(currentYear, currentMonth + 1, 0)
    const lastDayOfMonthNumber = lastDayOfMonthDate.getDate()
    const dateRange = default_language === "en"
        ? ref(`${currentMonth + 1}-01-${currentYear} to ${currentMonth + 1}-${lastDayOfMonthNumber}-${currentYear}`)
        : ref(`01-${currentMonth + 1}-${currentYear} to ${lastDayOfMonthNumber}-${currentMonth + 1}-${currentYear}`)

    const perPage = ref(10)
    const currentPage = ref(1)
    const sortBy = ref('rawDate')
    const isSortDirDesc = ref(false)

    const translatedOpinionOptions = computed(() => {
        return stateOpinionOptions.value.map(option => ({
          label: i18n.t(`label.${option}`), 
          value: option
        }))
      })

    const clearFilters = () => {
        dateRange.value = ''
        selectedLocations.value = ''
        selectedPhysicalLocations.value = ''
        selectedFeedbackPoints.value = ''
        searchQuery.value = ''
        selectedOpinion.value = '' 
    }

    const tableColumns = computed(() => {
        return [
            { key: 'rawDate', label: i18n.t('message.tableHeader.date'), sortable: true, thStyle: { width: '10%' }, formatter: (_, __, item) => item.date },
            { key: 'physical_location', label: i18n.t('label.Physical Location'), sortable: true, thStyle: { width: '10%' } },
            { key: 'location', label: i18n.t('message.tableHeader.location'), sortable: true, thStyle: { width: '10%' } },
            { key: 'feedbackPoint', label: i18n.t('message.tableHeader.feedbackPoint'), sortable: true, thStyle: { width: '10%' } },
            { key: 'feedback', label: i18n.t('message.tableHeader.feedback'), sortable: true, thStyle: { width: '10%' } },
            { key: 'detail', label: i18n.t('message.tableHeader.detail'), sortable: true },
            { key: 'actions', label: i18n.t('message.tableHeader.actions'), thStyle: { width: '15%' } },
        ]
    })

    const listCustomersOpinions = async (component, date, location, physical_location, feedbackPoint, searchValue) => {
        if (searchValue && searchValue !== searchQuery.value) return // To avoid early updates of the search when the user is still typing
        
        isLoading.value = true

        const query = {
            client_id: oid(clientId),
            deleted: { $ne: true }
        }

        if (location) {
            query.location = oid(location)
        }

        if (physical_location) {
            query.physical_location = oid(physical_location)
        }

        if (feedbackPoint) {
            query.feedbackPoint = oid(feedbackPoint)
        }

        if (selectedOpinion.value?.value) {
            query.feedback = selectedOpinion.value.value
        }

        if (date) {
            if (component === 'new') {
                const isoActualDate = new Date(date);
                const isoSevenDaysBefore = new Date(date)
        
                isoActualDate.setDate(date.getDate() + 1) // Increment by one day to include the entire current day
                isoSevenDaysBefore.setDate(date.getDate() - 7) // Search data from today to one week ago
                
                query.date = { $gte: mongoDate(isoSevenDaysBefore), $lt: mongoDate(isoActualDate) }
            } else if (component === 'list') {
                // Date Range: by default it comes with 'to', but if you click on the same number for the from date and the to date, it arrives without 'to'
                let from, to
                if (date.includes(' to ')) {
                    const datesArray = date.split(" ")
                    from = datesArray[0].split("-")
                    to = datesArray[2].split("-")
                }   else {
                    from = to = date.split("-")
                }
                // Ensure the date is converted to the start and end of the day for accurate filtering
                const fromDate = new Date(parseDate(from))
                const toDate = new Date(parseDate(to))
                toDate.setHours(23, 59, 59, 999)

                query.date = { $gte: mongoDate(fromDate), $lt: mongoDate(toDate) }
            }
        }

        const pipeline = [
            { $match: query },
            { $lookup: { from: 'location', localField: 'location', foreignField: '_id', pipeline: [ { $project: { location: 1 } } ], as: 'location' } },
            { $lookup: { from: 'physical_location', localField: 'physical_location', foreignField: '_id', pipeline: [ { $project: { name: 1 } } ], as: 'physical_location' } },
            { $lookup: { from: 'feedback_point', localField: 'feedbackPoint', foreignField: '_id', pipeline: [ { $project: { name: 1 } } ], as: 'feedbackPoint' } },
            { $addFields: { _id: { $toString: "$_id" }, location: { $arrayElemAt: ["$location", 0] },  physical_location: { $arrayElemAt: ["$physical_location", 0] },  feedbackPoint: { $arrayElemAt: ["$feedbackPoint", 0] } } },
            { $sort: { date: 1 } },
        ]

        try {
            let backData = await getItemsAggregate({ collection, pipeline })

            if (searchQuery.value) {
                backData = backData?.filter(e => e.detail?.toLowerCase().includes(searchQuery.value.toLowerCase()))
            }

            customersOpinions.value = backData
                ? await Promise.all(backData.map(e => {
                    e.rawDate = new Date(e.date)
                    e.date = formatIsoDate(e.date, true)
                    if (e.detail?.length > 100) {
                        e.croppedDetail = e.detail.substring(0, 100) + '...'
                    }
                    const textColor =
                        e.feedback === 'positive' ? 'success'
                        : e.feedback === 'negative' ? 'danger'
                        : 'warning'
                        e.textColor = textColor
                    return e
                }))
                : []

            totalOpinions.value = customersOpinions.value.length
            parseDataForExcel(customersOpinions.value)
        } catch (error) {
            console.log(error)
            showErrorMessage(i18n.t('message.err_opinions_list'))
        } finally {
            if (!searchValue || searchValue === searchQuery.value) isLoading.value = false
        }
    }

    const parseDate = (dateRange) => {
        const objDate = new Date(dateRange[2], dateRange[default_language === "en" ? 0 : 1] - 1, dateRange[default_language === "en" ? 1 : 0])
        const year = objDate.getFullYear()
        const month = objDate.getMonth()
        const day = objDate.getDate()
        const date = new Date(year, month, day)
        return date
    }  

    const parseDataForExcel = (customersOpinions) => {
        const parseCustomersOpinions = customersOpinions.map(o => {
            const { location, date, feedback, detail, physical_location, feedbackPoint } = o
            return {
                location: location.location,
                date,
                feedback: i18n.t(`label.${feedback}`),
                detail,
                physical_location: physical_location?.name || '',
                feedbackPoint: feedbackPoint?.name || ''  }
        });
        dataExcel.value = parseCustomersOpinions
    }

    // Fields to download the excel
    const customerOpinionsFields = ref({
        [i18n.t('message.tableHeader.location')]: "location",
        [i18n.t('label.Physical Location')]: "physical_location",
        [i18n.t('label.feedbackPoint')]: "feedbackPoint",
        [i18n.t('message.tableHeader.date')]: "date",
        [i18n.t('message.tableHeader.feedback')]: "feedback",
        [i18n.t('message.tableHeader.detail')]: "detail",
    })

    const searchInDetail = searchValue => {
        isLoading.value = true
        listCustomersOpinions('list', dateRange.value, selectedLocations.value, selectedPhysicalLocations.value, selectedFeedbackPoints.value, searchValue)
    }

    const showingMessage = computed(() => {
        const total = totalOpinions.value
        const from = ((currentPage.value - 1) * perPage.value) + 1
        const to = Math.min(currentPage.value * perPage.value, total)
        return i18n.tc('message.paginationText', 0, { from, to, total })
    })
    
    const datePickerConfig = ref({
        mode: "range",
        dateFormat: default_language === "en" ? "m-d-Y" : "d-m-Y",
    })
    
    const handleView = (id) => {
        opinionId.value = opinionId.value === id ? null : id
    }

    const deleteCustomerOpinion = async (id) => {
        isLoading.value = true

        const query = { _id: oid(id) }
        const action = { $set: { deleted: true } }

        try {
            await updateItem({ collection, query, action })
    
            showSuccessMessage(i18n.t('message.customerOpinionRemoved'))
            listCustomersOpinions('list', dateRange.value, selectedLocations.value, selectedPhysicalLocations.value, selectedFeedbackPoints.value)
        } catch (error) {
            console.log(error)
            showErrorMessage(i18n.t('message.opinion_remove_error'))
        } finally {
            isLoading.value = false
        }
    }

    onMounted(() => {
        getLocationsForDropDown()
        getPhysicalLocationsForDropDown()
        getFeedbackPointsForDropDown()
    })


    watch([dateRange, selectedLocations, selectedPhysicalLocations, selectedFeedbackPoints, selectedOpinion], () => {
        listCustomersOpinions('list', dateRange.value, selectedLocations.value, selectedPhysicalLocations.value, selectedFeedbackPoints.value)
    },{ deep: true })

    watch([sortBy, isSortDirDesc], () => {
        currentPage.value = 1
    })
    
return {
        isOnline,
        isLoading,
        showingMessage,
        locations,
        physical_locations,
        feedbackPoints,
        searchQuery,
        customersOpinions,
        tableColumns,
        sortBy,
        isSortDirDesc,
        perPage,
        totalOpinions,
        currentPage,
        dateRange,
        datePickerConfig,
        selectedLocations,
        selectedPhysicalLocations,
        selectedFeedbackPoints,
        listCustomersOpinions,
        router,
        deleteCustomerOpinion,
        searchInDetail,
        handleView,
        opinionId,
        clearFilters,
        selectedOpinion,
        translatedOpinionOptions,
        dataExcel,
        customerOpinionsFields,
        role
    }

}